import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Box, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';
import { createStyles, withStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import { DateConverter } from '../../utils/DateConverter';
import RedChip from '../../reusable-component/RedChip';
import GreenChip from '../../reusable-component/GreenChip';
import YellowChip from '../../reusable-component/YellowChip';
import PaginationFooter from '../../reusable-component/PaginationFooter';
import BasicMenu from '../../reusable-component/BasicMenu';
import { getEngagementByUserGroupAndClientIdApi } from "../../api's/EngagementApi";
import LoadingIcon from '../../reusable-component/LoadingIcon';
import { AuthContext } from '../../context/AuthProvider';
import { HighlightMatch } from '../../utils/HighLightSearchedKeyword';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    background: 'none !important',
    color: '#8798AD',
    padding: '10px',
    textTransform: 'uppercase',
    fontSize: 12,
    fontWeight: 600,
    textAlign: 'center',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: '15px',
    textAlign: 'center',
  },
}));

const StyledTableRow = withStyles((theme) =>
  createStyles({
    root: {
      border: '1px solid red',
      background: '#FFFFFF',
      boxShadow: '0px 2px 6px #0000000A',
      borderRadius: '5px !important',
    },
  })
)(TableRow);

export default function ClientTable({
  tableData,
  fetchTableData,
  currentPage,
  setPage,
  search,
  handleChangePage,
  totalCount,
  pagination = false,
  favouriteClients,
  toggleFavoriteClient,
}) {
  const { userGroup } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const rowsPerPage = 5;

  const navigateToClient = async (row) => {
    setLoading(true);
    sessionStorage.removeItem('filtered_clientdetails_search');
    sessionStorage.removeItem('filtered_clientdetails_status');
    sessionStorage.removeItem('filtered_clientdetails_search1');
    sessionStorage.removeItem('filtered_clientdetails_status1');
    sessionStorage.removeItem('tab_value_clientDetails');
    try {
      const response = await getEngagementByUserGroupAndClientIdApi(userGroup, row.client_id);
      if (response.status === 200) {
        const path_url = '/client/view_details';
        const stateObj = {
          client_id: row.client_id,
          client_name: row.client_name,
          domain_name: row.domain_name,
          region: row.region,
          engagement_count: row.engagement_count,
          workflow_count: row.workflow_count,
          created_by: row.created_by,
          created_at: row.created_at,
          status: row.status,
        };
        navigate(path_url, {
          state: stateObj,
        });
        sessionStorage.setItem('prev_path', JSON.stringify(path_url));
        sessionStorage.setItem('state_obj', JSON.stringify(stateObj));
      }
    } catch (error) {
      console.log(error);
      if (error.response?.status === 500) {
        enqueueSnackbar('500 Internal Server Error!', {
          variant: 'error',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
      } else if (error.response?.status === 404) {
        enqueueSnackbar('404 Engagement not found!', {
          variant: 'error',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
      } else {
        enqueueSnackbar('Something went wrong!', {
          variant: 'error',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
      }
    }
    setLoading(false);
  };

  return (
    <Box>
      <TableContainer>
        <Table
          aria-label="simple table"
          sx={{
            minWidth: 650,
            borderSpacing: '0px 4px',
            borderCollapse: 'separate',
          }}
        >
          <TableHead>
            <TableRow>
              <StyledTableCell>Client Name</StyledTableCell>
              <StyledTableCell>Region</StyledTableCell>
              <StyledTableCell>Total Engagements</StyledTableCell>
              <StyledTableCell>Total Workflows</StyledTableCell>
              <StyledTableCell>Created On</StyledTableCell>
              <StyledTableCell>Created By</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Action</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(pagination ? tableData.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage) : tableData).map(
              (row) => (
                <StyledTableRow key={row.client_id}>
                  <StyledTableCell>
                    {row.status === 'approved' ? (
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => {
                          navigateToClient(row);
                        }}
                        sx={{ textDecoration: 'none', color: '#1976D2' }}
                      >
                        {HighlightMatch(row.client_name, search)}
                      </Link>
                    ) : (
                      row.client_name
                    )}
                  </StyledTableCell>
                  <StyledTableCell>{row.region}</StyledTableCell>
                  <StyledTableCell>{row.engagement_count}</StyledTableCell>
                  <StyledTableCell>{row.workflow_count}</StyledTableCell>
                  <StyledTableCell>{DateConverter(row.created_at)}</StyledTableCell>
                  <StyledTableCell>{row.created_by}</StyledTableCell>
                  <StyledTableCell>
                    {row.status === 'approved' ? (
                      <GreenChip title="Approved" />
                    ) : row.status === 'pending' ? (
                      <YellowChip title="Pending" />
                    ) : (
                      <RedChip title="Rejected" />
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    <BasicMenu
                      row={row}
                      fetchData={fetchTableData}
                      favouriteClients={favouriteClients}
                      toggleFavoriteClient={toggleFavoriteClient}
                      type="Client"
                    />
                  </StyledTableCell>
                </StyledTableRow>
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <PaginationFooter
        totalCount={totalCount}
        tableData={tableData}
        page={currentPage}
        handleChangePage={handleChangePage}
        pagination={pagination}
        disabled={loading}
      />

      {loading && <LoadingIcon />}
    </Box>
  );
}
